import { useMemo } from 'react'
import { cn } from '@/lib/utils'
import type { IdeologyVector } from '@/types'
import { getQuadrantInfo, getPositionDescription } from '@/lib/ideology2d'
import { QUADRANT_ICONS } from '@/constants/politicalMap'
import type { QuadrantName } from '@/constants/politicalMap'

// ============================================================================
// Types
// ============================================================================

interface TooltipEntity {
  id: string
  name: string
  ideology: IdeologyVector
  color?: string
  type: 'party' | 'candidate'
}

interface PoliticalMapTooltipProps {
  entity: TooltipEntity
  position: { x: number; y: number }
  className?: string
}

// ============================================================================
// Component
// ============================================================================

export function PoliticalMapTooltip({
  entity,
  position,
  className,
}: PoliticalMapTooltipProps) {
  const quadrantInfo = getQuadrantInfo(entity.ideology)
  const positionDesc = getPositionDescription(quadrantInfo.coordinates)
  const icon = QUADRANT_ICONS[quadrantInfo.quadrantName as QuadrantName] || ''

  // Calculate tooltip position to keep it on screen
  const tooltipPos = useMemo(() => {
    const padding = 16
    const tooltipWidth = 280
    const tooltipHeight = 120

    let x = position.x + padding
    let y = position.y + padding

    // Check right boundary
    if (typeof window !== 'undefined' && x + tooltipWidth > window.innerWidth - padding) {
      x = position.x - tooltipWidth - padding
    }

    // Check bottom boundary
    if (typeof window !== 'undefined' && y + tooltipHeight > window.innerHeight - padding) {
      y = position.y - tooltipHeight - padding
    }

    // Ensure minimum position
    x = Math.max(padding, x)
    y = Math.max(padding, y)

    return { x, y }
  }, [position])

  return (
    <div
      className={cn(
        'fixed z-50 pointer-events-none',
        'bg-popover border border-border rounded-lg shadow-xl',
        'animate-in fade-in-0 zoom-in-95 duration-150',
        className
      )}
      style={{
        left: tooltipPos.x,
        top: tooltipPos.y,
        minWidth: 220,
        maxWidth: 280,
      }}
    >
      {/* Header */}
      <div className="px-3 py-2 border-b border-border">
        <div className="flex items-center gap-2">
          {/* Color indicator */}
          <span
            className="w-3 h-3 rounded-full flex-shrink-0"
            style={{ backgroundColor: entity.color || '#6366f1' }}
          />

          {/* Name */}
          <span className="font-semibold text-sm truncate">
            {entity.name}
          </span>

          {/* Type badge */}
          <span className={cn(
            'ml-auto text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded',
            entity.type === 'party' ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
          )}>
            {entity.type}
          </span>
        </div>
      </div>

      {/* Body */}
      <div className="px-3 py-2 space-y-1.5">
        {/* Quadrant */}
        <div className="flex items-start gap-2">
          <span className="text-base">{icon}</span>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-foreground">
              {quadrantInfo.quadrantName}
            </div>
            <div className="text-xs text-muted-foreground">
              {quadrantInfo.subLabel}
            </div>
          </div>
        </div>

        {/* Position description */}
        <div className="text-xs text-muted-foreground pt-1 border-t border-border/50">
          {positionDesc}
        </div>

        {/* Coordinates */}
        <div className="flex gap-3 text-xs">
          <span className={cn(
            'tabular-nums',
            quadrantInfo.coordinates.x < 0 ? 'text-red-400' : quadrantInfo.coordinates.x > 0 ? 'text-blue-400' : 'text-muted-foreground'
          )}>
            H: {quadrantInfo.coordinates.x > 0 ? '+' : ''}{quadrantInfo.coordinates.x}
          </span>
          <span className={cn(
            'tabular-nums',
            quadrantInfo.coordinates.y > 0 ? 'text-orange-400' : quadrantInfo.coordinates.y < 0 ? 'text-green-400' : 'text-muted-foreground'
          )}>
            V: {quadrantInfo.coordinates.y > 0 ? '+' : ''}{quadrantInfo.coordinates.y}
          </span>
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// Simplified Tooltip for Quick Info
// ============================================================================

interface QuickTooltipProps {
  name: string
  quadrantName: string
  color?: string
}

export function PoliticalMapQuickTooltip({
  name,
  quadrantName,
  color,
}: QuickTooltipProps) {
  const icon = QUADRANT_ICONS[quadrantName as QuadrantName] || ''

  return (
    <div className="flex items-center gap-2 px-2 py-1 bg-popover border border-border rounded shadow-lg">
      <span
        className="w-2 h-2 rounded-full"
        style={{ backgroundColor: color || '#6366f1' }}
      />
      <span className="text-xs font-medium">{name}</span>
      <span className="text-xs text-muted-foreground">
        {icon} {quadrantName}
      </span>
    </div>
  )
}
